import { RegisterDismissedSectionTexts } from '../../../../../types/PartyApplication/PartyApplicationTexts';
import {
  MAX_TERMINATED_EMPLOYEES,
  TerminatedEmployeesValidationMessages,
} from './utils/validation';
import { TerminatedEmployeesWarningMessages } from './utils/getEmployeeWarnings';

export type RegisterDismissedUiTexts = {
  title: string;
  preamble: string;
  addButton: string;
  importButton: string;
  downloadTemplateButton: string;
  downloadCopyButton: string;
  templateFileUrl: string;
  importSuccess: string;
  importErrorFallback: string;
  importUnreadableFile: string;
  importNoPeople: string;
  importMissingColumns: string;
  importMaxRowsWarning: string;
  importUnknownEmploymentType: string;
  importInvalidDate: string;
  allCorrectedInfo: string;
  replaceDialogTitle: string;
  replaceDialogDescription: string;
  replaceDialogAccept: string;
  replaceDialogCancel: string;
  filterAll: string;
  filterErrors: string;
  filterWarnings: string;
  filterErrorsEmptyInfo: string;
  filterWarningsEmptyInfo: string;
  searchPlaceholder: string;
  columnSsn: string;
  columnFirstName: string;
  columnLastName: string;
  columnEmploymentType: string;
  columnStartDate: string;
  columnTerminationDate: string;
  columnEndDate: string;
  columnEmail: string;
  columnPhone: string;
  removeRowLabel: string;
};

const DEFAULT_PREAMBLE =
  '<div>Fyll i nedan eller ladda upp de uppsagda, du kan ladda upp en egen exportfil eller använda vår mall för att säkerställa att uppgifterna läses in korrekt.</div>';

export const replaceTemplateTokens = (
  value: string,
  tokens: Record<string, string | number>
): string =>
  Object.entries(tokens).reduce(
    (result, [key, tokenValue]) =>
      result.split(`{${key}}`).join(String(tokenValue)),
    value
  );

export const resolveRegisterDismissedUiTexts = (
  texts: RegisterDismissedSectionTexts | null | undefined
): RegisterDismissedUiTexts => {
  const temporaryImportErrorMessage =
    texts?.registerDismissedImportErrorFallback ??
    'Det uppstod ett tillfälligt fel. Försök igen.';
  const fileImportErrorMessage =
    texts?.registerDismissedImportUnreadableFile ??
    'Filen kunde inte läsas. Kontrollera att det är en giltig Excel-fil (.xlsx).';
  const noPeopleImportErrorMessage =
    texts?.registerDismissedImportNoPeople ?? 'Filen innehåller inga personer.';

  return {
    title: texts?.registerDismissedHeadline ?? 'Registrera uppsagda',
    preamble: texts?.registerDismissedPreamble ?? DEFAULT_PREAMBLE,
    addButton: texts?.registerDismissedAddBtn ?? 'Lägg till uppsagd',
    importButton: texts?.registerDismissedImportFileBtn ?? 'Importera fil',
    downloadTemplateButton:
      texts?.registerDismissedDownloadTemplateBtn ?? 'Ladda ner mall',
    downloadCopyButton:
      texts?.registerDismissedDownloadCopyBtn ?? 'Ladda ner kopia',
    templateFileUrl: texts?.registerDismissedTemplateFileUrl ?? '',
    importSuccess:
      texts?.registerDismissedImportSuccess ?? 'Importen lyckades.',
    importErrorFallback: temporaryImportErrorMessage,
    importUnreadableFile: fileImportErrorMessage,
    importNoPeople: noPeopleImportErrorMessage,
    importMissingColumns:
      texts?.registerDismissedImportMissingColumns ??
      'Följande kolumner saknas i filen: {missingColumns}. Använd TSL:s deltagarlista.',
    importMaxRowsWarning:
      texts?.registerDismissedImportMaxRowsWarning ??
      'Filen innehåller {totalRows} personer. De första {maxRows} importeras. Skicka in en till ansökan med resterande deltagare.',
    importUnknownEmploymentType:
      texts?.registerDismissedImportUnknownEmploymentType ??
      'Okänd anställningsform "{value}". Välj anställningsform manuellt.',
    importInvalidDate:
      texts?.registerDismissedImportInvalidDate ??
      'Datumet "{value}" kunde inte tolkas. Ange datum manuellt.',
    allCorrectedInfo:
      texts?.registerDismissedAllCorrectedInfo ??
      'Alla fel är korrigerade. Välj <strong>Alla</strong> för att se hela listan.',
    replaceDialogTitle:
      texts?.registerDismissedReplaceDialogTitle ??
      'Ersätt registrerade personer?',
    replaceDialogDescription:
      texts?.registerDismissedReplaceDialogDescription ??
      'Du har redan {count} registrerade personer. Importen ersätter listan med innehållet i filen.',
    replaceDialogAccept:
      texts?.registerDismissedReplaceDialogAccept ?? 'Ersätt listan',
    replaceDialogCancel:
      texts?.registerDismissedReplaceDialogCancel ?? 'Avbryt',
    filterAll: texts?.registerDismissedFilterAll ?? 'Alla {count}',
    filterErrors:
      texts?.registerDismissedFilterErrors ?? 'Behöver korrigeras {count}',
    filterWarnings:
      texts?.registerDismissedFilterWarnings ?? 'Att kontrollera {count}',
    filterErrorsEmptyInfo:
      texts?.registerDismissedFilterErrorsEmptyInfo ||
      texts?.registerDismissedAllCorrectedInfo ||
      'Alla fel är korrigerade. Välj <strong>Alla</strong> för att se hela listan.',
    filterWarningsEmptyInfo:
      texts?.registerDismissedFilterWarningsEmptyInfo ||
      'Inga personer har uppgifter att kontrollera just nu. Välj <strong>Alla</strong> för att se hela listan.',
    searchPlaceholder:
      texts?.registerDismissedSearchPlaceholder ??
      'Sök på personnummer eller namn',
    columnSsn: texts?.registerDismissedColumnSsn ?? 'Personnummer',
    columnFirstName: texts?.registerDismissedColumnFirstName ?? 'Förnamn',
    columnLastName: texts?.registerDismissedColumnLastName ?? 'Efternamn',
    columnEmploymentType:
      texts?.registerDismissedColumnEmploymentType ?? 'Anst.form',
    columnStartDate: texts?.registerDismissedColumnStartDate ?? 'Startdatum',
    columnTerminationDate:
      texts?.registerDismissedColumnTerminationDate ?? 'Uppsagd',
    columnEndDate: texts?.registerDismissedColumnEndDate ?? 'Slutdatum',
    columnEmail: texts?.registerDismissedColumnEmail ?? 'E-post',
    columnPhone: texts?.registerDismissedColumnPhone ?? 'Mobil',
    removeRowLabel: texts?.registerDismissedRemoveRowLabel ?? 'Ta bort {name}',
  };
};

export const resolveRegisterDismissedValidationMessages = (
  texts: RegisterDismissedSectionTexts | null | undefined
): TerminatedEmployeesValidationMessages => ({
  dateFormat:
    texts?.registerDismissedValidationDateFormat ?? 'Ange datum som ÅÅÅÅ-MM-DD',
  ssnLength:
    texts?.registerDismissedValidationSsnLength ??
    'Personnummer måste vara 12 tecken',
  ssnInvalid:
    texts?.registerDismissedValidationSsnInvalid ?? 'Ogiltigt personnummer',
  firstNameRequired:
    texts?.registerDismissedValidationFirstNameRequired ?? 'Ange förnamn',
  lastNameRequired:
    texts?.registerDismissedValidationLastNameRequired ?? 'Ange efternamn',
  employmentTypeRequired:
    texts?.registerDismissedValidationEmploymentTypeRequired ??
    'Välj anställningsform',
  startDateRequired:
    texts?.registerDismissedValidationStartDateRequired ?? 'Ange startdatum',
  terminationDateRequired:
    texts?.registerDismissedValidationTerminationDateRequired ??
    'Ange uppsägningsdatum',
  endDateRequired:
    texts?.registerDismissedValidationEndDateRequired ?? 'Ange slutdatum',
  emailInvalid:
    texts?.registerDismissedValidationEmailInvalid ??
    'Ange en giltig e-postadress',
  phoneInvalid:
    texts?.registerDismissedValidationPhoneInvalid ?? 'Ogiltigt telefonnummer',
  terminationBeforeStart:
    texts?.registerDismissedValidationTerminationBeforeStart ??
    'Uppsägningsdatum kan inte vara före startdatum',
  endBeforeTermination:
    texts?.registerDismissedValidationEndBeforeTermination ??
    'Slutdatum kan inte vara före uppsägningsdatum',
  endBeforeStart:
    texts?.registerDismissedValidationEndBeforeStart ??
    'Slutdatum kan inte vara före startdatum',
  minEmployees:
    texts?.registerDismissedValidationMinEmployees ??
    'Lägg till minst en uppsagd person',
  maxEmployees: replaceTemplateTokens(
    texts?.registerDismissedValidationMaxEmployees ??
      'Högst {max} personer kan registreras per ansökan. Om ni har fler, skicka in en till ansökan.',
    { max: MAX_TERMINATED_EMPLOYEES }
  ),
  duplicateSsn:
    texts?.registerDismissedValidationDuplicateSsn ??
    'Personnumret förekommer flera gånger i listan',
  duplicateEmail:
    texts?.registerDismissedValidationDuplicateEmail ??
    'E-postadressen förekommer flera gånger i listan',
  duplicatePhone:
    texts?.registerDismissedValidationDuplicatePhone ??
    'Telefonnumret förekommer flera gånger i listan',
});

export const resolveRegisterDismissedWarningMessages = (
  texts: RegisterDismissedSectionTexts | null | undefined
): TerminatedEmployeesWarningMessages => ({
  noContact:
    texts?.registerDismissedWarningNoContact ??
    'Utan kontaktinfo kan det bli svårare att nå personen',
  missingEmploymentDateIfUnionRep:
    texts?.registerDismissedWarningMissingEmploymentDateIfUnionRep ??
    'Om du inte har anställningsdatum kan du skicka in ändå, så kontaktar vi arbetsgivaren',
  noNoticePeriod:
    texts?.registerDismissedWarningNoNoticePeriod ??
    'Ingen uppsägningstid? Ange anställningens sista dag även om arbetsbefriad.',
  sameStartAndTerminationDate:
    texts?.registerDismissedWarningSameStartAndTerminationDate ??
    'Startdatum och uppsägningsdatum är samma. Kontrollera att datumen stämmer.',
  sameStartAndEndDate:
    texts?.registerDismissedWarningSameStartAndEndDate ??
    'Startdatum och slutdatum är samma. Kontrollera att datumen stämmer.',
  futureEndDateTemplate:
    texts?.registerDismissedWarningFutureEndDate ??
    'Anställd t o m ligger mer än {months} månader fram i tiden. Kontrollera datumet.',
});
